import { useCallback, useState } from 'react';

import type { FirstClassDocument, PartialTtsData } from '../../types';
import nowTimestamp from '../../utils/dates/nowTimestamp';
import { isBookwise } from '../../utils/env.platform';
import getServerBaseUrl from '../../utils/getServerBaseUrl.platform';
import makeLogger from '../../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import requestWithAuth from '../../utils/requestWithAuth.platformIncludingExtension';
import ttsController from '../actions/ttsController.platform';
import database from '../database';
import { globalState } from '../models';
import background from '../portalGates/toBackground';
import { fetchDocumentContent } from '../stateUpdaters/transientStateUpdaters/documentContent';

const logger = makeLogger(__filename);

export async function fetchWordBoundaries() {
  if (isBookwise) {
    return;
  }
  const { tts } = globalState.getState();
  if (!tts) {
    return;
  }
  const { playingDocId } = tts;
  background.pollLatestState(1);
  fetchDocumentContent([playingDocId], true);
}

/**
 * Bookwise-only: Fetches word boundaries from the server endpoint and merges into cache.
 * Throttled to at most once every 5 seconds.
 */
let lastFetchTime = 0;
export async function fetchWordBoundariesForCache() {
  const now = nowTimestamp();
  if (now - lastFetchTime < 5000) {
    return;
  }
  lastFetchTime = now;

  const { tts } = globalState.getState();
  if (!tts) {
    return;
  }
  const { playingDocId, playingChunkId } = tts;

  const doc = await database.collections.documents.findOne<FirstClassDocument>(playingDocId);
  if (!doc) {
    return;
  }

  const voice = ttsController.getVoiceForDocument(doc);

  const url = new URL(`${getServerBaseUrl()}/reader/api/documents/${playingDocId}/tts_word_boundaries`);
  url.searchParams.set('voice', voice);
  if (playingChunkId) {
    url.searchParams.set('chunkId', playingChunkId);
  }

  try {
    const response = await requestWithAuth(url.toString());
    if (!response.ok) {
      logger.error('TTS word boundaries endpoint returned non-OK status', { status: response.status, playingDocId });
      return;
    }
    // claude eslint: response.json() returns unknown; cast to known server response shape
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const data = await response.json() as { tts: PartialTtsData | null; };
    if (data.tts === null) {
      return;
    }

    const voiceData = data.tts[voice];
    if (voiceData) {
      await background.mergeTtsWordBoundaries(playingDocId, voice, voiceData);
    }
  } catch (error) {
    logger.error('Failed to fetch TTS word boundaries:', { error });
  }
}

export function useFetchWordBoundariesThrottled() {
  // This code is in charge of making a network request every 5 seconds for word boundaries if we are missing them
  const [isFetchingWordBoundaries, setIsFetchingWordBoundaries] = useState(false);

  return useCallback(() => {
    if (isFetchingWordBoundaries) {
      return;
    }
    setIsFetchingWordBoundaries(true);
    fetchWordBoundaries();
    setTimeout(() => {
      setIsFetchingWordBoundaries(false);
    }, 5000);
  }, [isFetchingWordBoundaries]);
}
